import { Button, Checkbox, Input, Modal, Space, Typography } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { KeypointCollectionLabel } from 'models/keypoints';
import {
  ScrollableList,
  SelectButtonsRow,
  InfoTextRow,
  CheckboxRow,
  CommentBox,
} from './RejectModal.styled';

const { Text } = Typography;

interface IProps {
  isModalOpen: boolean;
  handleCancel: () => void;
  handleOk: (acceptedKeypoints: string[], comment: string) => void;
  segmentations: KeypointCollectionLabel[]; // reuse prop name shape
  initialAcceptedIds?: string[];
}

const AcceptModal: FC<IProps> = props => {
  const { isModalOpen, handleCancel, handleOk, segmentations, initialAcceptedIds = [] } = props;
  const [comment, setComment] = useState('');
  const [acceptedKeypoints, setAcceptedKeypoints] = useState<Set<string>>(
    new Set(initialAcceptedIds)
  );

  useEffect(() => {
    if (isModalOpen) {
      // default to selecting all keypoints for faster technician reviews
      const allIds = segmentations.map(s => s.icid);
      setAcceptedKeypoints(new Set(allIds));
      setComment('');
    }
  }, [isModalOpen, initialAcceptedIds]);

  const handleCheckboxChange = (id: string, checked: boolean) => {
    setAcceptedKeypoints(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    const allIds = segmentations.map(s => s.icid);
    setAcceptedKeypoints(new Set(allIds));
  };

  const handleSelectNone = () => setAcceptedKeypoints(new Set());

  return (
    <Modal
      title="Select Keypoints to Accept"
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <SelectButtonsRow>
            <Button size="small" onClick={handleSelectAll} style={{ marginRight: 8 }}>
              Select All
            </Button>
            <Button size="small" onClick={handleSelectNone}>
              Select None
            </Button>
          </SelectButtonsRow>
          <InfoTextRow>
            <Text type="secondary">
              Only selected keypoints will be accepted; others will remain unchanged.
            </Text>
          </InfoTextRow>
        </div>

        <ScrollableList>
          {segmentations.map(kp => {
            const isAccepted = acceptedKeypoints.has(kp.icid);
            return (
              <CheckboxRow key={kp.icid}>
                <Checkbox
                  checked={isAccepted}
                  onChange={e => handleCheckboxChange(kp.icid, e.target.checked)}
                >
                  <Space direction="vertical" size={0}>
                    <Text strong>{kp.keypointCollectionClassValue}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {kp.icid}
                      {isAccepted && ' (Currently Accepted)'}
                    </Text>
                  </Space>
                </Checkbox>
              </CheckboxRow>
            );
          })}
        </ScrollableList>

        <CommentBox>
          <Text strong>Comment (optional):</Text>
          <Input.TextArea
            showCount
            rows={3}
            value={comment}
            onChange={e => setComment(e.target.value)}
            maxLength={255}
            placeholder="Add a comment..."
            style={{ marginTop: 8, resize: 'none' }}
          />
        </CommentBox>

        <Space style={{ width: '100%', justifyContent: 'space-between' }} size={12}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={() => handleOk(Array.from(acceptedKeypoints), comment)}
            disabled={acceptedKeypoints.size === 0}
          >
            Accept ({acceptedKeypoints.size} selected)
          </Button>
        </Space>
      </Space>
    </Modal>
  );
};

export default AcceptModal;
